import React from "react";
import { motion } from "framer-motion";
import { Sparkles, ArrowUpRight } from "lucide-react";

const modules = [
  "Admission Management",
  "Finance & Fee",
  "LMS",
  "Assessments",
  "HR & Staff",
  "Transport & GPS",
  "Mobile App",
  "Attendance",
  "Timetable",
  "Communication",
  "Library",
  "Hostel",
  "Inventory",
  "Visitor",
  "Report Cards",
  "Parent Portal",
  "Examinations",
  "Calendar",
  "Discipline",
  "Analytics",
];

const EdKonect = () => {
  return (
    <section
      id="products"
      data-testid="edkonect-section"
      className="py-24 lg:py-32 bg-[#0a0a0a] text-white relative overflow-hidden grain"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="grid grid-cols-12 gap-x-3 gap-y-8 lg:gap-12 items-start">
          <div className="col-span-12 lg:col-span-5">
            <div className="flex items-center gap-3 mb-6">
              <span className="font-mono text-xs text-[#5b8bff]">[ 04 ]</span>
              <span className="text-xs uppercase tracking-[0.25em] font-semibold text-[#5b8bff]">
                Flagship Product
              </span>
            </div>

            <a
              href="https://edkonect.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="edkonect-external-badge"
              className="group inline-flex items-center gap-2 bg-[#0038FF] px-4 py-2 mb-8 hover:bg-white hover:text-[#0038FF] transition-colors"
            >
              <Sparkles className="w-4 h-4" strokeWidth={2} />
              <span className="text-xs uppercase tracking-[0.2em] font-bold">
                EdKonect
              </span>
              <ArrowUpRight
                className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                strokeWidth={2.25}
              />
            </a>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-testid="edkonect-heading"
              className="font-display text-[2.4rem] sm:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.05] mb-8 break-words"
            >
              India&apos;s smart campus,
              <br />
              <span className="italic font-medium text-[#5b8bff]">
                unified.
              </span>
            </motion.h2>

            <p className="text-lg text-white/75 leading-relaxed max-w-md mb-10">
              A single platform that replaces the patchwork of school software.
              Twenty integrated modules. Built for principals, parents, teachers
              and trustees — all at once.
            </p>

            <div className="grid grid-cols-3 gap-6 border-t border-white/15 pt-8 mb-10">
              <div>
                <div className="font-display text-3xl font-black">20+</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60 mt-2">
                  Modules
                </div>
              </div>
              <div>
                <div className="font-display text-3xl font-black">60%</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60 mt-2">
                  Admin Workload Cut
                </div>
              </div>
              <div>
                <div className="font-display text-3xl font-black">5+</div>
                <div className="text-[10px] font-mono uppercase tracking-[0.18em] text-white/60 mt-2">
                  Indian States
                </div>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="https://edkonect.com/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="edkonect-visit-cta"
                className="inline-flex items-center gap-3 bg-white text-[#0a0a0a] px-7 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-[#0038FF] hover:text-white transition-colors duration-300"
              >
                Visit edkonect.com
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.25} />
              </a>
              <a
                href="#contact"
                data-testid="edkonect-cta"
                className="inline-flex items-center gap-3 border-2 border-white/40 text-white px-7 py-4 text-sm font-semibold uppercase tracking-wider hover:bg-white hover:text-[#0a0a0a] hover:border-white transition-colors duration-300"
              >
                Book a walkthrough
                <ArrowUpRight className="w-4 h-4" strokeWidth={2.25} />
              </a>
            </div>
          </div>

          <div className="col-span-12 lg:col-span-7">
            <div className="border border-white/15 p-6 md:p-8">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-white/15">
                <span className="font-mono text-xs uppercase tracking-[0.2em] text-white/60">
                  Integrated Modules
                </span>
                <span className="font-mono text-xs text-[#5b8bff]">
                  /20
                </span>
              </div>
              <div className="flex flex-wrap gap-2 md:gap-3">
                {modules.map((m, idx) => (
                  <motion.span
                    key={m}
                    initial={{ opacity: 0, y: 8 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: idx * 0.025 }}
                    data-testid={`edkonect-module-${idx}`}
                    className="border border-white/25 px-3 py-1.5 md:px-4 md:py-2 text-xs md:text-sm font-medium text-white/85 hover:bg-[#0038FF] hover:border-[#0038FF] hover:text-white transition-colors cursor-default"
                  >
                    {m}
                  </motion.span>
                ))}
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
              {[
                { k: "Discovery", v: "Audit & blueprint" },
                { k: "Configuration", v: "Module setup" },
                { k: "Go-Live", v: "Training & rollout" },
              ].map((s) => (
                <div
                  key={s.k}
                  data-testid={`edkonect-process-${s.k.toLowerCase()}`}
                  className="border border-white/15 p-5"
                >
                  <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5b8bff] mb-2">
                    {s.k}
                  </div>
                  <div className="text-sm text-white/85">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default EdKonect;
